/**
 * As planilhas que o caixa exporta.
 *
 * Papel: aqui ficam `planilhaDosLancamentos`, que transforma as linhas da
 * tabela de lançamentos na `Planilha` do "Exportar XLSX" (l. 2807–2839), com o
 * nome do arquivo e o aviso da origem, e `planilhaDosCadastros`, com as abas
 * "Plano de Contas" e "Favorecidos" do "Exportar XLSX" de Cadastros
 * (l. 2841–2867).
 *
 * Como usar: as linhas entram na ordem da tabela e saem na mesma ordem; a
 * planilha não ordena nem filtra. O valor entra como número — positivo no que
 * entra no caixa, negativo no que sai — e o saldo corrido como número, vazio no
 * pendente, para quem abrir o arquivo poder somar as colunas. A data entra como
 * data do Excel, mostrada como `dd/mm/aaaa`, que é o que a origem escrevia em
 * texto. O nome do arquivo e o aviso usam o "hoje" de quem chama, que é
 * `hojeEmIso`, em hora local (exceção 1).
 */
import {
  type Categoria,
  type Favorecido,
  type LancamentoDaListagem,
  ROTULOS_FORMA,
  arredondarCentavos,
  entraNoCaixa,
  valorDoLancamento,
} from '@concierge/shared';

import { formatarData } from '@/shared/lib/formato';
import { type Planilha } from '@/shared/lib/planilha';
import { type Periodo } from './filtros-dos-lancamentos';
import { STATUS_NA_PLANILHA, TIPO_NA_PLANILHA } from './rotulos';

/** Os cabeçalhos da aba "Lançamentos", na ordem da origem. */
const CABECALHOS_DOS_LANCAMENTOS = [
  'Data',
  'Favorecido',
  'Descrição',
  'Categoria',
  'Subcategoria',
  'Forma de Pagamento',
  'Tipo',
  'Valor (R$)',
  'Status',
  'Saldo (R$)',
];

/** A largura de cada coluna, em caracteres (l. 2832). */
const LARGURAS_DOS_LANCAMENTOS = [12, 22, 35, 22, 22, 18, 14, 14, 14, 14];

/**
 * Tira os traços de uma data ISO, para o nome do arquivo.
 *
 * @param iso - A data, `AAAA-MM-DD`.
 * @returns `AAAAMMDD`.
 */
function compacta(iso: string): string {
  return iso.replace(/-/g, '');
}

/**
 * Monta a planilha das linhas da tabela.
 *
 * @param linhas - As linhas à vista, na ordem da tabela.
 * @param periodo - O "de" e o "até" dos filtros, vazios quando não preenchidos.
 * @param hoje - A data de hoje, em hora local, no lugar da ponta que faltar.
 * @returns A planilha, com a aba "Lançamentos" e o arquivo
 *   `lancamentos_AAAAMMDD_AAAAMMDD.xlsx`.
 */
export function planilhaDosLancamentos(
  linhas: readonly LancamentoDaListagem[],
  periodo: Periodo,
  hoje: string,
): Planilha {
  return {
    nomeDoArquivo: `lancamentos_${compacta(periodo.de === '' ? hoje : periodo.de)}_${compacta(periodo.ate === '' ? hoje : periodo.ate)}.xlsx`,
    abas: [
      {
        nome: 'Lançamentos',
        cabecalhos: CABECALHOS_DOS_LANCAMENTOS,
        larguras: LARGURAS_DOS_LANCAMENTOS,
        linhas: linhas.map((linha) => {
          const valor = arredondarCentavos(valorDoLancamento(linha));
          return [
            { data: linha.data },
            linha.favorecido?.nome ?? null,
            linha.descricao,
            linha.categoria,
            linha.subcategoria,
            linha.formaPagamento === null ? null : ROTULOS_FORMA[linha.formaPagamento],
            TIPO_NA_PLANILHA[linha.tipo],
            entraNoCaixa(linha.tipo) ? valor : -valor,
            STATUS_NA_PLANILHA[linha.status],
            linha.saldoCorrido,
          ];
        }),
      },
    ],
  };
}

/**
 * O aviso depois de exportar, com o período dos filtros (l. 2810–2811, 2837).
 *
 * Sem período, o aviso diz "todos os períodos" mesmo quando a tabela mostra um
 * mês só, como na origem.
 *
 * @param quantos - Quantas linhas foram para a planilha.
 * @param periodo - O "de" e o "até" dos filtros.
 * @returns `✓ Exportado: N lançamentos (DD/MM/AAAA a DD/MM/AAAA)`.
 */
export function avisoDaExportacao(quantos: number, periodo: Periodo): string {
  let texto = 'todos os períodos';
  if (periodo.de !== '' && periodo.ate !== '') {
    texto = `${formatarData(periodo.de)} a ${formatarData(periodo.ate)}`;
  } else if (periodo.de !== '') {
    texto = `a partir de ${formatarData(periodo.de)}`;
  } else if (periodo.ate !== '') {
    texto = `até ${formatarData(periodo.ate)}`;
  }
  return `✓ Exportado: ${String(quantos)} lançamentos (${texto})`;
}

/**
 * Monta a planilha dos cadastros.
 *
 * @param plano - O plano de contas, em ordem alfabética.
 * @param favorecidos - Os favorecidos, em ordem alfabética.
 * @param hoje - A data de hoje, em hora local, para o nome do arquivo.
 * @returns A planilha com a aba "Plano de Contas" — uma linha por subcategoria,
 *   e a categoria sem nenhuma numa linha com a subcategoria vazia — e a aba
 *   "Favorecidos", com "Em uso" ou "Cadastrado"; arquivo `cadastros_AAAAMMDD.xlsx`.
 */
export function planilhaDosCadastros(
  plano: readonly Categoria[],
  favorecidos: readonly Favorecido[],
  hoje: string,
): Planilha {
  return {
    nomeDoArquivo: `cadastros_${compacta(hoje)}.xlsx`,
    abas: [
      {
        nome: 'Plano de Contas',
        cabecalhos: ['Categoria', 'Subcategoria'],
        larguras: [28, 32],
        linhas: plano.flatMap((categoria) =>
          categoria.subcategorias.length === 0
            ? [[categoria.nome, null]]
            : categoria.subcategorias.map((subcategoria) => [
                categoria.nome,
                subcategoria.nome,
              ]),
        ),
      },
      {
        nome: 'Favorecidos',
        cabecalhos: ['Nome', 'Status'],
        larguras: [30, 14],
        linhas: favorecidos.map((favorecido) => [
          favorecido.nome,
          favorecido.emUso ? 'Em uso' : 'Cadastrado',
        ]),
      },
    ],
  };
}
